import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Save,
  Image,
  Type,
  Layout,
  Share2,
  Plus,
  Trash2,
  Info,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Heart,
  Users,
  Clock,
  Star,
  Zap,
  Globe,
  Award,
  Upload,
  BarChart3,
  FileText,
  Link2,
  Unlink,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  Mail,
  KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { refreshBranding } from '../../hooks/useBranding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available icons for features
const availableIcons = [
  { value: 'Shield', label: 'Shield', icon: Shield },
  { value: 'Lock', label: 'Lock', icon: Lock },
  { value: 'Eye', label: 'Eye', icon: Eye },
  { value: 'Heart', label: 'Heart', icon: Heart },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'Clock', label: 'Clock', icon: Clock },
  { value: 'Star', label: 'Star', icon: Star },
  { value: 'Zap', label: 'Zap', icon: Zap },
  { value: 'Globe', label: 'Globe', icon: Globe },
  { value: 'Award', label: 'Award', icon: Award },
];

// Animation types for trust stats
const animationTypes = [
  { value: 'count_up', label: 'Count Up (0 → value)' },
  { value: 'count_down', label: 'Count Down (value → 0)' },
  { value: 'static', label: 'Static (no animation)' },
];

// Zoho Books Integration Card Component
const ZohoIntegrationCard = () => {
  const [zohoStatus, setZohoStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchZohoStatus();
    
    // Check URL params for OAuth callback result
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('zoho_success') === 'connected') {
      toast.success('Zoho Books connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchZohoStatus();
    } else if (urlParams.get('zoho_error')) {
      toast.error(`Zoho connection failed: ${urlParams.get('zoho_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchZohoStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/zoho/status`, {
        headers: getAuthHeader()
      });
      setZohoStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Zoho status:', error);
      setZohoStatus({ connected: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const response = await axios.get(`${API}/admin/zoho/connect`, {
        headers: getAuthHeader()
      });
      
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      console.error('Failed to initiate Zoho connection:', error);
      toast.error('Failed to connect to Zoho Books');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Zoho Books? This will stop invoice generation.')) {
      return;
    }

    setIsDisconnecting(true);
    try {
      await axios.post(`${API}/admin/zoho/disconnect`, {}, {
        headers: getAuthHeader()
      });
      setZohoStatus({ connected: false });
      toast.success('Zoho Books disconnected');
    } catch (error) {
      console.error('Failed to disconnect Zoho:', error);
      toast.error('Failed to disconnect Zoho Books');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <CardTitle className="text-white">Zoho Books Integration</CardTitle>
            <CardDescription className="text-slate-400">
              Generate GST-compliant invoices automatically
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </div>
        ) : zohoStatus?.connected ? (
          <div className="space-y-4">
            {/* Connected Status */}
            <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <div className="flex-1">
                <p className="text-green-400 font-medium">Connected</p>
                <p className="text-slate-400 text-sm">
                  Organization: {zohoStatus.organization_name || 'Unknown'}
                </p>
              </div>
            </div>

            {/* Info */}
            <div className="p-4 bg-slate-800/50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Organization ID</span>
                <span className="text-white font-mono">{zohoStatus.organization_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Connected On</span>
                <span className="text-white">
                  {zohoStatus.connected_at 
                    ? new Date(zohoStatus.connected_at).toLocaleDateString() 
                    : 'Unknown'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => window.open('https://books.zoho.in', '_blank')}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Zoho Books
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-500/30"
              >
                {isDisconnecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Unlink className="w-4 h-4 mr-2" />
                )}
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Not Connected Status */}
            <div className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              <XCircle className="w-5 h-5 text-slate-500" />
              <div className="flex-1">
                <p className="text-slate-300 font-medium">Not Connected</p>
                <p className="text-slate-500 text-sm">
                  Connect your Zoho Books account to generate invoices
                </p>
              </div>
            </div>

            {/* Benefits */}
            <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
              <p className="text-amber-400 font-medium text-sm mb-2">Benefits of connecting:</p>
              <ul className="text-slate-400 text-sm space-y-1">
                <li>• Auto-generate GST-compliant invoices</li>
                <li>• Invoices auto-marked as paid</li>
                <li>• Partners & clients can download PDFs</li>
                <li>• Centralized accounting in Zoho Books</li>
              </ul>
            </div>

            {/* Connect Button */}
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
              className="w-full bg-green-600 hover:bg-green-500 text-white"
            >
              {isConnecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Connect Zoho Books
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Email Settings Card Component
const EmailSettingsCard = () => {
  const [fromName, setFromName] = useState('WillKart.ai');
  const [fromEmail, setFromEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: getAuthHeader()
      });
      setFromName(response.data.from_name || 'WillKart.ai');
      setFromEmail(response.data.from_email || '');
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fromName.trim()) {
      toast.error('Sender name cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/email-settings`, {
        from_name: fromName.trim()
      }, {
        headers: getAuthHeader()
      });
      toast.success('Email settings updated successfully');
    } catch (error) {
      console.error('Failed to update email settings:', error);
      toast.error('Failed to update email settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <CardTitle className="text-white">Email Settings</CardTitle>
            <CardDescription className="text-slate-400">
              Customize how emails appear to recipients
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </div>
        ) : (
          <>
            <div>
              <Label className="text-slate-300 mb-2 block">Sender Name</Label>
              <div className="flex gap-3">
                <Input
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white flex-1"
                  placeholder="WillKart.ai"
                  data-testid="email-from-name-input"
                />
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="save-email-settings-btn"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-slate-500 text-xs mt-1">
                This name will appear as the sender in all emails
              </p>
            </div>
            
            <div className="p-4 bg-slate-800/50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Sender Email</span>
                <span className="text-white">{fromEmail}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Preview</span>
                <span className="text-blue-400">{fromName} &lt;{fromEmail}&gt;</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Change Password Card Component
const ChangePasswordCard = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const rules = [
    { label: 'At least 10 characters', test: (p) => p.length >= 10 },
    { label: 'One uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
    { label: 'One number (0-9)', test: (p) => /[0-9]/.test(p) },
    { label: 'One special character (!@#$…)', test: (p) => /[^A-Za-z0-9]/.test(p) },
  ];

  const handleSubmit = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    const failed = rules.find((r) => !r.test(newPassword));
    if (failed) {
      toast.error(`Password requirement not met: ${failed.label}`);
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to change password';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="admin-change-password-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <CardTitle className="text-white">Change Admin Password</CardTitle>
            <CardDescription className="text-slate-400">
              Rotate your admin password securely. Requires current password.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-slate-300 mb-2 block">Current Password</Label>
          <div className="relative">
            <Input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              className="bg-slate-800 border-slate-700 text-white pr-10"
              data-testid="admin-current-password-input"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              aria-label="Toggle current password visibility"
            >
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 mb-2 block">New Password</Label>
            <div className="relative">
              <Input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-slate-800 border-slate-700 text-white pr-10"
                data-testid="admin-new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                aria-label="Toggle new password visibility"
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-slate-300 mb-2 block">Confirm New Password</Label>
            <div className="relative">
              <Input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="bg-slate-800 border-slate-700 text-white pr-10"
                data-testid="admin-confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                aria-label="Toggle confirm password visibility"
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <p className="text-slate-300 text-sm font-medium mb-2">Password requirements:</p>
          <ul className="space-y-1">
            {rules.map((rule, idx) => {
              const passed = newPassword ? rule.test(newPassword) : false;
              return (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  {passed ? (
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  )}
                  <span className={passed ? 'text-green-400' : 'text-slate-400'}>{rule.label}</span>
                </li>
              );
            })}
            {confirmPassword && (
              <li className="flex items-center gap-2 text-sm">
                {newPassword === confirmPassword ? (
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                )}
                <span className={newPassword === confirmPassword ? 'text-green-400' : 'text-red-400'}>
                  Passwords match
                </span>
              </li>
            )}
          </ul>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={isSaving}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          data-testid="admin-change-password-submit"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <KeyRound className="w-4 h-4 mr-2" />
          )}
          {isSaving ? 'Updating...' : 'Change Password'}
        </Button>
      </CardContent>
    </Card>
  );
};

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);

  // File input refs
  const logoMainRef = useRef(null);
  const logoSmallRef = useRef(null);
  const howItWorksRefs = useRef({});

  // Fetch settings
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Upload file
  const handleFileUpload = async (file, fieldName, index = null) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadingField(fieldName + (index !== null ? `-${index}` : ''));

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileUrl = `${BACKEND_URL}${response.data.url}`;

      if (index !== null) {
        // How It Works image
        updateHowItWorks(index, 'image', fileUrl);
      } else {
        // Logo fields
        updateField(fieldName, fileUrl);
      }

      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingField(null);
    }
  };

  // Save settings
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      await refreshBranding();
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Update a single field
  const updateField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  // Update trust stat
  const updateTrustStat = (index, field, value) => {
    setSettings((prev) => {
      const stats = [...(prev.trust_stats || [])];
      stats[index] = { ...stats[index], [field]: value };
      
      // Auto-extract numeric value from display value if it changes
      if (field === 'value') {
        const numericMatch = value.replace(/[^0-9]/g, '');
        if (numericMatch) {
          stats[index].numeric_value = parseInt(numericMatch, 10);
        }
      }
      
      return { ...prev, trust_stats: stats };
    });
  };

  // Add trust stat
  const addTrustStat = () => {
    setSettings((prev) => ({
      ...prev,
      trust_stats: [
        ...(prev.trust_stats || []),
        { id: Date.now().toString(), value: '', label: '', type: 'static', numeric_value: 0 },
      ],
    }));
  };

  // Remove trust stat
  const removeTrustStat = (index) => {
    setSettings((prev) => ({
      ...prev,
      trust_stats: prev.trust_stats.filter((_, i) => i !== index),
    }));
  };

  // Update feature
  const updateFeature = (index, field, value) => {
    setSettings((prev) => {
      const features = [...prev.features];
      features[index] = { ...features[index], [field]: value };
      return { ...prev, features };
    });
  };

  // Add feature
  const addFeature = () => {
    setSettings((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        { id: Date.now().toString(), icon: 'Shield', title: '', description: '' },
      ],
    }));
  };

  // Remove feature
  const removeFeature = (index) => {
    setSettings((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // Update How It Works item
  const updateHowItWorks = (index, field, value) => {
    setSettings((prev) => {
      const items = [...prev.how_it_works];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, how_it_works: items };
    });
  };

  // Add How It Works item
  const addHowItWorks = () => {
    const newStep = ((settings.how_it_works?.length || 0) + 1).toString().padStart(2, '0');
    setSettings((prev) => ({
      ...prev,
      how_it_works: [
        ...prev.how_it_works,
        { id: Date.now().toString(), step: newStep, title: '', description: '', image: '' },
      ],
    }));
  };

  // Remove How It Works item
  const removeHowItWorks = (index) => {
    setSettings((prev) => ({
      ...prev,
      how_it_works: prev.how_it_works.filter((_, i) => i !== index),
    }));
  };

  // Update social link
  const updateSocialLink = (platform, value) => {
    setSettings((prev) => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value },
    }));
  };

  // Tooltip with info icon
  const InfoTooltip = ({ content }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-4 h-4 text-slate-500 hover:text-slate-300 cursor-help ml-2" />
        </TooltipTrigger>
        <TooltipContent className="bg-slate-800 border-slate-700 text-white max-w-xs">
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Image upload component
  const ImageUpload = ({ value, onChange, onUpload, fieldName, index, tooltip, isUploading }) => (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter image URL or upload"
          className="flex-1 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
        />
        <input
          type="file"
          accept="image/*"
          className="hidden"
          ref={(el) => {
            if (index !== null && index !== undefined) {
              howItWorksRefs.current[index] = el;
            }
          }}
          onChange={(e) => onUpload(e.target.files[0])}
        />
        <Button
          type="button"
          variant="outline"
          disabled={isUploading}
          onClick={() => {
            if (index !== null && index !== undefined) {
              howItWorksRefs.current[index]?.click();
            } else if (fieldName === 'logo_main') {
              logoMainRef.current?.click();
            } else if (fieldName === 'logo_small') {
              logoSmallRef.current?.click();
            }
          }}
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          {isUploading ? (
            <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </Button>
      </div>
      {value && (
        <div className="p-3 bg-slate-800 rounded-lg">
          <img src={value} alt="Preview" className="max-h-20 object-contain rounded" />
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center text-slate-400 py-16">
        Failed to load settings. Please refresh the page.
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Hidden file inputs */}
      <input
        type="file"
        accept="image/*"
        className="hidden"
        ref={logoMainRef}
        onChange={(e) => handleFileUpload(e.target.files[0], 'logo_main')}
      />
      <input
        type="file"
        accept="image/*"
        className="hidden"
        ref={logoSmallRef}
        onChange={(e) => handleFileUpload(e.target.files[0], 'logo_small')}
      />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <p className="text-slate-400 mt-1">Customize your website content and appearance</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {/* Admin Account Security */}
      <ChangePasswordCard />

      {/* Logo Settings */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Image className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-white">Logo Settings</CardTitle>
              <CardDescription className="text-slate-400">
                Upload your brand logos
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center mb-2">
                <Label className="text-slate-300">Main Logo</Label>
                <InfoTooltip content="Best results: wide logo with transparent background (e.g., 600×180 PNG, ~3.3:1 ratio). Square logos also work but may appear smaller. Displayed at 48–64px tall across the site." />
              </div>
              <ImageUpload
                value={settings.logo_main}
                onChange={(val) => updateField('logo_main', val)}
                onUpload={(file) => handleFileUpload(file, 'logo_main')}
                fieldName="logo_main"
                isUploading={uploadingField === 'logo_main'}
              />
              {settings.logo_main && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = settings.logo_main;
                    link.download = 'willkart-logo-main.png';
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Downloading main logo...');
                  }}
                  data-testid="download-main-logo"
                >
                  <Upload className="w-4 h-4 mr-2 rotate-180" /> Download Main Logo
                </Button>
              )}
            </div>
            <div>
              <div className="flex items-center mb-2">
                <Label className="text-slate-300">Small Logo (Favicon/Collapsed Sidebar)</Label>
                <InfoTooltip content="Square image, 512×512 PNG recommended. Used as the browser favicon and in collapsed sidebars (rendered at 32–40px)." />
              </div>
              <ImageUpload
                value={settings.logo_small}
                onChange={(val) => updateField('logo_small', val)}
                onUpload={(file) => handleFileUpload(file, 'logo_small')}
                fieldName="logo_small"
                isUploading={uploadingField === 'logo_small'}
              />
              {settings.logo_small && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = settings.logo_small;
                    link.download = 'willkart-logo-small.png';
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Downloading small logo...');
                  }}
                  data-testid="download-small-logo"
                >
                  <Upload className="w-4 h-4 mr-2 rotate-180" /> Download Small Logo
                </Button>
              )}
            </div>
          </div>

          {/* Live Brand Preview */}
          {(settings.logo_main || settings.logo_small) && (
            <div className="mt-4 p-5 rounded-xl border border-slate-700 bg-slate-950/60">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-300 text-sm font-semibold uppercase tracking-wider">Live Preview</p>
                <p className="text-slate-500 text-xs">How your logo appears across the app</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Navbar preview (h-12) */}
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                  <p className="text-slate-500 text-xs mb-3">Navbar / Sidebar header</p>
                  <div className="h-16 flex items-center px-4 bg-slate-900 border border-slate-800 rounded-lg">
                    {settings.logo_main ? (
                      <img src={settings.logo_main} alt="logo" className="h-12 max-w-[240px] object-contain" />
                    ) : (
                      <span className="text-slate-500 text-sm">no logo uploaded</span>
                    )}
                  </div>
                </div>
                {/* Login card preview (h-16) */}
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                  <p className="text-slate-500 text-xs mb-3">Login / Reset page</p>
                  <div className="py-6 flex items-center justify-center bg-slate-900 border border-slate-800 rounded-lg">
                    {settings.logo_main ? (
                      <img src={settings.logo_main} alt="logo" className="h-16 max-w-[280px] object-contain" />
                    ) : (
                      <span className="text-slate-500 text-sm">no logo uploaded</span>
                    )}
                  </div>
                </div>
                {/* Favicon preview */}
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                  <p className="text-slate-500 text-xs mb-3">Favicon / Collapsed sidebar</p>
                  <div className="h-16 flex items-center justify-center bg-slate-900 border border-slate-800 rounded-lg gap-2">
                    {settings.logo_small ? (
                      <img src={settings.logo_small} alt="favicon" className="w-10 h-10 rounded-lg object-contain" />
                    ) : (
                      <span className="text-slate-500 text-sm">no logo uploaded</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex gap-2">
                <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-xs leading-relaxed">
                  <strong>Tip:</strong> If your logo appears too small, it likely has transparent/coloured padding inside the image. Crop the logo tightly to its edges before uploading, or export at a wide aspect ratio (e.g. 600×180 PNG). Square logos with padding will always look smaller than a tightly-cropped wordmark.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hero Banner Settings */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Type className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-white">Hero Banner</CardTitle>
              <CardDescription className="text-slate-400">
                Customize the main hero section text
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-slate-300 mb-2 block">Headline</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input
                  value={settings.hero_headline_before}
                  onChange={(e) => updateField('hero_headline_before', e.target.value)}
                  placeholder="Before italic text"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                />
                <p className="text-xs text-slate-500 mt-1">Before italic</p>
              </div>
              <div>
                <Input
                  value={settings.hero_headline_italic}
                  onChange={(e) => updateField('hero_headline_italic', e.target.value)}
                  placeholder="Italic/highlighted text"
                  className="bg-slate-800/50 border-slate-700 text-amber-400 placeholder:text-slate-500 focus:border-amber-500 italic"
                />
                <p className="text-xs text-slate-500 mt-1">Highlighted (italic)</p>
              </div>
              <div>
                <Input
                  value={settings.hero_headline_after}
                  onChange={(e) => updateField('hero_headline_after', e.target.value)}
                  placeholder="After italic text"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                />
                <p className="text-xs text-slate-500 mt-1">After italic</p>
              </div>
            </div>
            <div className="mt-3 p-4 bg-slate-800 rounded-lg">
              <p className="text-lg text-white">
                Preview: {settings.hero_headline_before}{' '}
                <span className="text-amber-400 italic">{settings.hero_headline_italic}</span>
                {settings.hero_headline_after}
              </p>
            </div>
          </div>

          <div>
            <Label className="text-slate-300 mb-2 block">Subtitle</Label>
            <Textarea
              value={settings.hero_subtitle}
              onChange={(e) => updateField('hero_subtitle', e.target.value)}
              placeholder="Enter hero subtitle"
              rows={3}
              className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Primary Button Text</Label>
              <Input
                value={settings.hero_cta_primary}
                onChange={(e) => updateField('hero_cta_primary', e.target.value)}
                placeholder="e.g., Get Started"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Secondary Button Text</Label>
              <Input
                value={settings.hero_cta_secondary}
                onChange={(e) => updateField('hero_cta_secondary', e.target.value)}
                placeholder="e.g., Learn More"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
          </div>

          <Separator className="bg-slate-800" />

          <div>
            <Label className="text-slate-300 mb-2 block">Trust Text</Label>
            <Input
              value={settings.trust_text}
              onChange={(e) => updateField('trust_text', e.target.value)}
              placeholder="e.g., Trusted by families across India"
              className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
            />
          </div>
        </CardContent>
      </Card>

      {/* Trust Stats Section */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <BarChart3 className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <CardTitle className="text-white">Trust Statistics</CardTitle>
                <CardDescription className="text-slate-400">
                  Animated stats shown below the hero section
                </CardDescription>
              </div>
            </div>
            <Button
              onClick={addTrustStat}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Stat
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(settings.trust_stats || []).map((stat, index) => (
            <div
              key={stat.id || index}
              className="p-4 bg-slate-800/50 rounded-xl border border-slate-700"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-500 text-sm font-medium">Stat #{index + 1}</span>
                <Button
                  onClick={() => removeTrustStat(index)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Display Value</Label>
                  <Input
                    value={stat.value || ''}
                    onChange={(e) => updateTrustStat(index, 'value', e.target.value)}
                    placeholder="e.g., 10,000+"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Label</Label>
                  <Input
                    value={stat.label || ''}
                    onChange={(e) => updateTrustStat(index, 'label', e.target.value)}
                    placeholder="e.g., Wills Created"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Numeric Value</Label>
                  <Input
                    type="number"
                    value={stat.numeric_value || 0}
                    onChange={(e) => updateTrustStat(index, 'numeric_value', parseInt(e.target.value) || 0)}
                    placeholder="e.g., 10000"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Animation Type</Label>
                  <Select
                    value={stat.type || 'static'}
                    onValueChange={(value) => updateTrustStat(index, 'type', value)}
                  >
                    <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {animationTypes.map((type) => (
                        <SelectItem
                          key={type.value}
                          value={type.value}
                          className="text-white hover:bg-slate-700 cursor-pointer"
                        >
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
          {(!settings.trust_stats || settings.trust_stats.length === 0) && (
            <div className="text-center py-8 text-slate-500">
              No stats added yet. Click "Add Stat" to create one.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Features Section */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Layout className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <CardTitle className="text-white">Features Section</CardTitle>
                <CardDescription className="text-slate-400">
                  Manage the features displayed on the landing page
                </CardDescription>
              </div>
            </div>
            <Button
              onClick={addFeature}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.features.map((feature, index) => (
            <div
              key={feature.id}
              className="p-4 bg-slate-800/50 rounded-xl border border-slate-700"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 text-sm font-medium">#{index + 1}</span>
                  <Select
                    value={feature.icon}
                    onValueChange={(value) => updateFeature(index, 'icon', value)}
                  >
                    <SelectTrigger className="w-32 bg-slate-800 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {availableIcons.map((iconOption) => {
                        const IconComp = iconOption.icon;
                        return (
                          <SelectItem
                            key={iconOption.value}
                            value={iconOption.value}
                            className="text-white hover:bg-slate-700 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <IconComp className="w-4 h-4" />
                              {iconOption.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => removeFeature(index)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Title</Label>
                  <Input
                    value={feature.title}
                    onChange={(e) => updateFeature(index, 'title', e.target.value)}
                    placeholder="Feature title"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Description</Label>
                  <Input
                    value={feature.description}
                    onChange={(e) => updateFeature(index, 'description', e.target.value)}
                    placeholder="Feature description"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                  />
                </div>
              </div>
            </div>
          ))}
          {settings.features.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No features added yet. Click "Add Feature" to create one.
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works Section */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Layout className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white">How It Works Section</CardTitle>
                <CardDescription className="text-slate-400">
                  Manage the step-by-step guide on the landing page
                </CardDescription>
              </div>
            </div>
            <Button
              onClick={addHowItWorks}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Step
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.how_it_works.map((item, index) => (
            <div
              key={item.id}
              className="p-4 bg-slate-800/50 rounded-xl border border-slate-700"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-amber-500/30">{item.step}</span>
                </div>
                <Button
                  onClick={() => removeHowItWorks(index)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Title</Label>
                  <Input
                    value={item.title}
                    onChange={(e) => updateHowItWorks(index, 'title', e.target.value)}
                    placeholder="Step title"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm mb-1 block">Description</Label>
                  <Textarea
                    value={item.description}
                    onChange={(e) => updateHowItWorks(index, 'description', e.target.value)}
                    placeholder="Step description"
                    rows={2}
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 resize-none"
                  />
                </div>
                <div>
                  <div className="flex items-center mb-1">
                    <Label className="text-slate-400 text-sm">Image</Label>
                    <InfoTooltip content="Recommended size: 400x300 pixels. Use landscape images for best results. You can enter a URL or upload an image." />
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={item.image || ''}
                      onChange={(e) => updateHowItWorks(index, 'image', e.target.value)}
                      placeholder="Enter image URL or upload"
                      className="flex-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={(el) => (howItWorksRefs.current[index] = el)}
                      onChange={(e) => handleFileUpload(e.target.files[0], 'how_it_works', index)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={uploadingField === `how_it_works-${index}`}
                      onClick={() => howItWorksRefs.current[index]?.click()}
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      {uploadingField === `how_it_works-${index}` ? (
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  {item.image && (
                    <div className="mt-2">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-32 h-24 object-cover rounded-lg border border-slate-700"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {settings.how_it_works.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No steps added yet. Click "Add Step" to create one.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Social Media Links */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-pink-500/10">
              <Share2 className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <CardTitle className="text-white">Social Media Links</CardTitle>
              <CardDescription className="text-slate-400">
                Add your social media profile URLs for the footer
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-300 mb-2 block flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                </svg>
                LinkedIn
              </Label>
              <Input
                value={settings.social_links?.linkedin || ''}
                onChange={(e) => updateSocialLink('linkedin', e.target.value)}
                placeholder="https://linkedin.com/company/..."
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                YouTube
              </Label>
              <Input
                value={settings.social_links?.youtube || ''}
                onChange={(e) => updateSocialLink('youtube', e.target.value)}
                placeholder="https://youtube.com/@..."
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
                Instagram
              </Label>
              <Input
                value={settings.social_links?.instagram || ''}
                onChange={(e) => updateSocialLink('instagram', e.target.value)}
                placeholder="https://instagram.com/..."
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zoho Books Integration */}
      <ZohoIntegrationCard />

      {/* Email Settings */}
      <EmailSettingsCard />

      {/* Save Button (Bottom) */}
      <div className="flex justify-end pb-8">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
